class IVGREHeader extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.drawer = this.querySelector('[data-drawer]');
    this.overlay = this.querySelector('[data-overlay]');
    this.openButton = this.querySelector('[data-drawer-open]');
    this.closeButton = this.querySelector('[data-drawer-close]');
    this.searchToggle = this.querySelector('[data-search-toggle]');
    this.searchPanel = this.querySelector('[data-search-panel]');
    this.searchClose = this.querySelector('[data-search-close]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.lastFocus = null;
    this.lastSearchFocus = null;

    this.openButton?.addEventListener('click', () => this.openDrawer(), {signal});
    this.closeButton?.addEventListener('click', () => this.closeDrawer(), {signal});
    this.overlay?.addEventListener('click', () => this.closeDrawer(), {signal});
    this.searchToggle?.addEventListener('click', () => this.toggleSearch(), {signal});
    this.searchClose?.addEventListener('click', () => this.closeSearch(), {signal});
    this.querySelectorAll('[data-accordion]').forEach(btn => btn.addEventListener('click', () => this.toggleAccordion(btn), {signal}));
    document.addEventListener('keydown', e => this.onKeydown(e), {signal});
    if (this.dataset.sticky === 'true') window.addEventListener('scroll', () => this.onScroll(), {passive:true, signal});
    this.onScroll();
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.classList.contains('drawer-open')) document.body.style.overflow = '';
    this._connected = false;
  }

  openDrawer() {
    if (!this.drawer) return;
    this.closeSearch(false);
    this.lastFocus = document.activeElement;
    this.drawer.hidden = false;
    this.overlay.hidden = false;
    this.drawer.inert = false;
    this.drawer.removeAttribute('inert');
    this.drawer.setAttribute('aria-hidden','false');
    this.openButton?.setAttribute('aria-expanded','true');
    requestAnimationFrame(() => { this.drawer.classList.add('is-open'); this.overlay.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    const focusable = this.focusables(this.drawer);
    focusable[0]?.focus();
  }

  closeDrawer(restore = true) {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden','true');
    this.drawer.inert = true;
    this.drawer.setAttribute('inert','');
    this.openButton?.setAttribute('aria-expanded','false');
    document.body.style.overflow = '';
    if (this.overlay) this.overlay.hidden = true;
    if (restore) this.lastFocus?.focus();
  }

  toggleSearch() {
    if (!this.searchPanel) return;
    if (this.searchPanel.hidden) this.openSearch(); else this.closeSearch();
  }

  openSearch() {
    if (!this.searchPanel) return;
    if (this.drawer?.classList.contains('is-open')) this.closeDrawer(false);
    this.lastSearchFocus = document.activeElement;
    this.searchPanel.hidden = false;
    this.searchToggle?.setAttribute('aria-expanded','true');
    requestAnimationFrame(() => this.searchPanel.classList.add('is-open'));
    setTimeout(() => this.searchInput?.focus(), 40);
  }

  closeSearch(restore = true) {
    if (!this.searchPanel || this.searchPanel.hidden) return;
    this.searchPanel.classList.remove('is-open');
    this.searchPanel.hidden = true;
    this.searchToggle?.setAttribute('aria-expanded','false');
    if (restore) this.lastSearchFocus?.focus();
  }

  toggleAccordion(button) {
    const id = button.getAttribute('aria-controls');
    const panel = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!panel) return;
    const open = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!open));
    panel.hidden = open;
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      if (this.drawer?.classList.contains('is-open')) { this.closeDrawer(); return; }
      if (this.searchPanel && !this.searchPanel.hidden) this.closeSearch();
    }
    if (event.key === 'Tab' && this.drawer?.classList.contains('is-open')) {
      const items = this.focusables(this.drawer);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }

  onScroll() { if (this.dataset.sticky === 'true') this.classList.toggle('is-scrolled', window.scrollY > 20); }
  focusables(scope) { return Array.from(scope.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter(el => !el.closest('[hidden]')); }
}
if (!customElements.get('ivgre-header')) customElements.define('ivgre-header', IVGREHeader);

class IVGRESlideshow extends HTMLElement {
  connectedCallback() {
    if (this._connected) return; this._connected = true;
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.index = Math.max(0, this.slides.findIndex(s => s.classList.contains('is-active')));
    this.dotsWrap = this.querySelector('[data-dots]');
    if (this.dotsWrap && this.slides.length > 1) {
      this.slides.forEach((_, i) => { const b=document.createElement('button'); b.type='button'; b.className='slideshow-dot'+(i===this.index?' is-active':''); b.setAttribute('aria-label',`Go to slide ${i+1}`); b.addEventListener('click',()=>this.go(i)); this.dotsWrap.appendChild(b); });
    }
    this.querySelector('[data-prev]')?.addEventListener('click',()=>this.go(this.index-1));
    this.querySelector('[data-next]')?.addEventListener('click',()=>this.go(this.index+1));
    if (this.dataset.autoplay === 'true' && this.slides.length > 1 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.timer=setInterval(()=>this.go(this.index+1), Number(this.dataset.speed)||6000);
      this.addEventListener('mouseenter',()=>this.pause()); this.addEventListener('mouseleave',()=>this.resume());
    }
  }
  disconnectedCallback(){ clearInterval(this.timer); this._connected=false; }
  go(n){ if(!this.slides.length)return; this.slides[this.index]?.classList.remove('is-active'); this.index=(n+this.slides.length)%this.slides.length; this.slides[this.index]?.classList.add('is-active'); this.querySelectorAll('.slideshow-dot').forEach((d,i)=>d.classList.toggle('is-active',i===this.index)); }
  pause(){ if(this.timer){ clearInterval(this.timer); this.timer=null; } }
  resume(){ if(!this.timer && this.dataset.autoplay==='true') this.timer=setInterval(()=>this.go(this.index+1), Number(this.dataset.speed)||6000); }
}
if (!customElements.get('ivgre-slideshow')) customElements.define('ivgre-slideshow', IVGRESlideshow);

document.addEventListener('change', event => {
  const select = event.target.closest('[data-variant-select]');
  if (!select) return;
  const form = select.closest('form');
  const option = select.selectedOptions[0];
  const section = select.closest('[data-section-id]');
  const current = section?.querySelector('[data-current-price]');
  const compare = section?.querySelector('[data-compare-price]');
  const button = form?.querySelector('[data-add-to-cart]');
  const label = form?.querySelector('[data-add-label]');
  if (current && option.dataset.price) current.textContent = option.dataset.price;
  if (compare) { if(option.dataset.compare){ compare.hidden=false; compare.textContent=option.dataset.compare; } else compare.hidden=true; }
  const available = option.dataset.available === 'true';
  if (button) button.disabled = !available;
  if (label) label.textContent = available ? 'Add to cart' : 'Sold out';
});

const recommendationSections = document.querySelectorAll('[data-product-recommendations]');
recommendationSections.forEach(section => {
  const url = section.dataset.url;
  if (!url) return;
  fetch(url).then(r=>r.text()).then(html=>{ const doc=new DOMParser().parseFromString(html,'text/html'); const fresh=doc.querySelector('[data-product-recommendations]'); if(fresh && fresh.innerHTML.trim()) section.innerHTML=fresh.innerHTML; }).catch(()=>{});
});

document.documentElement.classList.remove('no-js');
